import { Space, Table, TableProps } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import Title from 'antd/lib/typography/Title';
import config from 'config';
import { format } from 'date-fns';
import { DonationOutput, DonationProductOutput } from 'gql/types';
import { getCryoType } from 'modules/products/utils/getCryoType';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

import { CryoTag } from '../CryoTag';
import DonationProductStatusBadge from '../DonationProductStatusBadge';

type PropTypes = {
  donation: DonationOutput;
  productCategories?: Record<number, string>;
} & TableProps<DonationProductOutput>;

const DonationProductsTable: FC<PropTypes> = ({
  donation,
  productCategories,
  ...restProps
}) => {
  const { t } = useTranslation('products.DonationProductDescriptions');
  const columns: ColumnsType<DonationProductOutput> = [
    {
      title: t('shortId'),
      dataIndex: 'shortId',
      key: 'shortId',
      width: '15%',
      align: 'center',
      render: (shortId: number) => shortId,
    },
    {
      title: t('productName'),
      dataIndex: 'productName',
      key: 'productName',
      width: '25%',
      align: 'center',
      render: (_, record: DonationProductOutput) => {
        const productName =
          productCategories?.[record.woocommerceCategoryId] ||
          t('productDonation');

        const cryoType = getCryoType(record.woocommerceProductId);

        return (
          <Space size={4}>
            <span>{productName || '-'}</span>
            {cryoType && <CryoTag type={cryoType} />}
          </Space>
        );
      },
    },
    {
      title: t('volume'),
      dataIndex: 'value',
      key: 'value',
      width: '15%',
      align: 'center',
      render: (value: number) => `${value} ml`,
    },

    {
      title: t('expires'),
      dataIndex: 'expirationDate',
      key: 'expirationDate',
      width: '20%',
      align: 'center',
      render: (expirationDate: string) =>
        format(new Date(expirationDate), config.DATE_FORMATS.DATE),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      align: 'center',
      render: (_, record) => (
        <DonationProductStatusBadge donationProduct={record} />
      ),
    },
  ];
  return (
    <Space direction="vertical" size={16} className={styles.tableWrapper}>
      <Title level={5}>
        {t('productsFromDonation', { donationId: donation.shortId })}
      </Title>

      <Table
        {...restProps}
        size="small"
        dataSource={donation.products || []}
        columns={columns}
        rowKey={(record) => record.id}
        pagination={false}
        bordered
        className={styles.table}
      />
    </Space>
  );
};

export default DonationProductsTable;
